import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, DollarSign, Activity, Calendar,
  BarChart3, Download, Sparkles, RefreshCw, Target, ChevronRight,
  Award, AlertCircle, CheckCircle, Clock, FileText, FileSpreadsheet,
  ArrowUpRight, ArrowDownRight, Lightbulb, ThumbsUp
} from 'lucide-react';
import ScheduledReportsManager from './ScheduledReportsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0D9488', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

const EnhancedReportsTab = () => {
  const [activeTab, setActiveTab] = useState('scorecard');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [scorecard, setScorecard] = useState(null);
  const [trends, setTrends] = useState({});
  const [aiInsights, setAiInsights] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // UI states
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState('revenue');
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiFocusArea, setAiFocusArea] = useState('overall');

  useEffect(() => {
    fetchScorecard();
    fetchAnalyticsOverview();
  }, []);

  useEffect(() => {
    if (activeTab === 'trends') {
      fetchTrend(selectedTrend);
    }
  }, [activeTab, selectedTrend]);

  const getDateRange = () => {
    const now = new Date();
    let start, end = now.toISOString();

    switch (dateRange) {
      case 'last_7_days':
        start = new Date(now.setDate(now.getDate() - 7)).toISOString();
        break;
      case 'last_30_days':
        start = new Date(now.setDate(now.getDate() - 30)).toISOString();
        break;
      case 'last_90_days':
        start = new Date(now.setDate(now.getDate() - 90)).toISOString();
        break;
      case 'custom':
        start = customStart;
        end = customEnd;
        break;
      default:
        start = new Date(now.setDate(now.getDate() - 30)).toISOString();
    }

    return { start, end };
  };

  const fetchScorecard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/advanced-reports/performance-scorecard`);
      setScorecard(response.data);
    } catch (error) {
      console.error('Failed to fetch scorecard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsOverview = async () => {
    try {
      const response = await axios.get(`${API}/reports/analytics/overview`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchTrend = async (metric) => {
    try {
      const response = await axios.get(`${API}/advanced-reports/trends/${metric}`, {
        params: { period: '12_months' }
      });
      setTrends(prev => ({ ...prev, [metric]: response.data }));
    } catch (error) {
      console.error(`Failed to fetch ${metric} trend:`, error);
    }
  };

  const fetchAiInsights = async () => {
    try {
      setAiLoading(true);
      const response = await axios.post(`${API}/advanced-reports/ai-insights`, {
        focus_area: aiFocusArea
      });
      setAiInsights(response.data);
      setShowAiDialog(true);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const generateReport = async (reportType) => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/${reportType}`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      });
      setReportData(response.data);
      toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated`);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (reportType, format) => {
    try {
      const { start, end } = getDateRange();
      const response = await axios.post(`${API}/reports/export/${format}`, {
        report_type: reportType,
        start_date: start,
        end_date: end,
        filters: {}
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`${format.toUpperCase()} report exported successfully`);
    } catch (error) {
      console.error('Failed to export report:', error);
      toast.error('Failed to export report');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-green-600 bg-green-100';
      case 'good': return 'text-blue-600 bg-blue-100';
      case 'needs_improvement': return 'text-amber-600 bg-amber-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'good': return <ThumbsUp className="h-5 w-5 text-blue-600" />;
      case 'needs_improvement': return <AlertCircle className="h-5 w-5 text-amber-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Advanced Reports & Analytics</h2>
          <p className="text-gray-600 mt-1">Performance insights and actionable recommendations</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={fetchAiInsights} 
            disabled={aiLoading}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            {aiLoading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            AI Insights
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4 mb-6">
          <TabsTrigger value="scorecard" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Scorecard
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Scheduled
          </TabsTrigger>
        </TabsList>

        {/* Scorecard Tab */}
        <TabsContent value="scorecard" className="space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : scorecard ? (
            <>
              {/* Overall Score */}
              <Card className="border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Overall Performance Score</h3>
                      <p className="text-gray-600 text-sm mt-1">Based on member retention, appointments, and revenue</p>
                    </div>
                    <div className="text-center">
                      <div className="text-6xl font-bold text-teal-600">{scorecard.overall_score}</div>
                      <div className="text-sm text-gray-500">out of 100</div>
                    </div>
                  </div>
                  <Progress value={scorecard.overall_score} className="mt-4 h-3" />
                </CardContent>
              </Card>

              {/* Individual Metrics */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(scorecard.scorecard).map(([key, metric]) => (
                  <Card key={key} className="relative overflow-hidden">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardDescription className="capitalize">
                          {key.replace(/_/g, ' ')}
                        </CardDescription>
                        {getStatusIcon(metric.status)}
                      </div>
                      <CardTitle className="text-3xl">
                        {metric.unit === '$' && formatCurrency(metric.value)}
                        {metric.unit === '%' && `${metric.value}%`}
                        {!metric.unit && metric.value}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Progress value={metric.score} className="h-2" />
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Score: {metric.score}/100</span>
                          <Badge className={getStatusColor(metric.status)}>
                            {metric.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        {metric.benchmark && (
                          <p className="text-xs text-gray-500">{metric.benchmark}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-amber-500" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {scorecard.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <ChevronRight className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Unable to load performance scorecard</p>
                <Button onClick={fetchScorecard} variant="outline" className="mt-4">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <CardTitle>Historical Trends</CardTitle>
                  <CardDescription>Track your performance over the last 12 months</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {['revenue', 'members', 'appointments'].map((metric) => (
                    <Button
                      key={metric}
                      variant={selectedTrend === metric ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedTrend(metric)}
                      className={selectedTrend === metric ? 'bg-teal-600 hover:bg-teal-700' : ''}
                    >
                      {metric.charAt(0).toUpperCase() + metric.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {trends[selectedTrend] ? (
                <div className="space-y-6">
                  {/* Trend Summary */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {trends[selectedTrend].trend_direction === 'up' ? (
                        <ArrowUpRight className="h-6 w-6 text-green-600" />
                      ) : trends[selectedTrend].trend_direction === 'down' ? (
                        <ArrowDownRight className="h-6 w-6 text-red-600" />
                      ) : (
                        <Activity className="h-6 w-6 text-gray-600" />
                      )}
                      <span className={`text-lg font-semibold ${
                        trends[selectedTrend].trend_direction === 'up' ? 'text-green-600' :
                        trends[selectedTrend].trend_direction === 'down' ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        Trending {trends[selectedTrend].trend_direction}
                      </span>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="text-sm text-gray-500">12-Month Total</div>
                      <div className="text-xl font-bold">
                        {selectedTrend === 'revenue' 
                          ? formatCurrency(trends[selectedTrend].total)
                          : trends[selectedTrend].total}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Monthly Average</div>
                      <div className="text-xl font-bold">
                        {selectedTrend === 'revenue'
                          ? formatCurrency(trends[selectedTrend].average)
                          : Math.round(trends[selectedTrend].average)}
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={trends[selectedTrend].trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => selectedTrend === 'revenue' ? formatCurrency(value) : value}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#0D9488" 
                        fill="#0D948820" 
                        strokeWidth={2}
                        name={selectedTrend.charAt(0).toUpperCase() + selectedTrend.slice(1)}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Date Range Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Date Range</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Select Period</label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="last_7_days">Last 7 Days</option>
                    <option value="last_30_days">Last 30 Days</option>
                    <option value="last_90_days">Last 90 Days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {dateRange === 'custom' && (
                  <>
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Start Date</label>
                      <input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">End Date</label>
                      <input
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      />
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          {analyticsData && (
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardDescription>Revenue (30 days)</CardDescription>
                  <CardTitle className="text-3xl">{formatCurrency(analyticsData.revenue.current)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {analyticsData.revenue.change_percent >= 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${analyticsData.revenue.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(analyticsData.revenue.change_percent).toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-600">vs previous period</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <CardDescription>Active Members</CardDescription>
                  <CardTitle className="text-3xl">{analyticsData.members.active}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-600">
                      {analyticsData.members.new_this_period} new this period
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-purple-500">
                <CardHeader>
                  <CardDescription>Appointments (30 days)</CardDescription>
                  <CardTitle className="text-3xl">{analyticsData.appointments.total}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <span className="text-sm text-gray-600">
                      {analyticsData.appointments.completed} completed
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Report Types */}
          <Card>
            <CardHeader>
              <CardTitle>Report Templates</CardTitle>
              <CardDescription>Generate detailed reports for different areas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { id: 'revenue', label: 'Revenue', icon: DollarSign, color: 'green' },
                  { id: 'membership', label: 'Membership', icon: Users, color: 'blue' },
                  { id: 'appointments', label: 'Appointments', icon: Calendar, color: 'purple' },
                  { id: 'patients', label: 'Patients', icon: Activity, color: 'amber' }
                ].map((report) => {
                  const Icon = report.icon;
                  return (
                    <button
                      key={report.id}
                      onClick={() => generateReport(report.id)}
                      className="p-6 rounded-lg border-2 border-gray-200 hover:border-teal-300 hover:bg-gray-50 transition-all text-left"
                    >
                      <Icon className="h-8 w-8 mb-3 text-gray-600" />
                      <h3 className="font-semibold text-gray-900 mb-1">{report.label}</h3>
                      <p className="text-sm text-gray-500">Generate report</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Report Results */}
          {loading && (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="animate-spin h-12 w-12 border-4 border-teal-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-600 mt-4">Generating report...</p>
              </CardContent>
            </Card>
          )}

          {reportData && !loading && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="capitalize">{reportData.report_type} Report</CardTitle>
                    <CardDescription>
                      {reportData.period?.start && new Date(reportData.period.start).toLocaleDateString()} - {reportData.period?.end && new Date(reportData.period.end).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => exportReport(reportData.report_type, 'pdf')}
                      className="bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => exportReport(reportData.report_type, 'excel')}
                      className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => exportReport(reportData.report_type, 'csv')}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                {reportData.summary && (
                  <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    {Object.entries(reportData.summary).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">
                          {typeof value === 'number' && key.includes('rate') ? `${value}%` :
                           typeof value === 'number' && (key.includes('revenue') || key.includes('transaction') || key.includes('average')) ? formatCurrency(value) :
                           value}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Charts based on report type */}
                {reportData.daily_breakdown && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Breakdown</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={Object.entries(reportData.daily_breakdown).map(([date, amount]) => ({
                        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        amount
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Line type="monotone" dataKey="amount" stroke="#0D9488" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {reportData.by_plan && (
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Members by Plan</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reportData.by_plan).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {Object.keys(reportData.by_plan).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {reportData.status_breakdown && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={Object.entries(reportData.status_breakdown).map(([name, value]) => ({ 
                            name: name.charAt(0).toUpperCase() + name.slice(1), 
                            value 
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#3B82F6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}

                {reportData.by_status && !reportData.by_plan && (
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">By Status</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(reportData.by_status).map(([name, value]) => ({ 
                              name: name.charAt(0).toUpperCase() + name.slice(1), 
                              value 
                            }))}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {Object.keys(reportData.by_status).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {reportData.by_type && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">By Type</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={Object.entries(reportData.by_type).map(([name, value]) => ({ name, value }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#8B5CF6" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scheduled Tab */}
        <TabsContent value="scheduled">
          <ScheduledReportsManager />
        </TabsContent>
      </Tabs>

      {/* AI Insights Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              AI-Powered Insights
            </DialogTitle>
            <DialogDescription>
              Personalized recommendations based on your clinic data
            </DialogDescription>
          </DialogHeader>

          {/* Focus Area Selector */}
          <div className="flex items-center gap-2 py-2">
            <span className="text-sm text-gray-600">Focus on:</span>
            {['overall', 'revenue', 'members', 'appointments'].map((area) => (
              <Button
                key={area}
                variant={aiFocusArea === area ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setAiFocusArea(area);
                  fetchAiInsights();
                }}
                className={aiFocusArea === area ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                {area.charAt(0).toUpperCase() + area.slice(1)}
              </Button>
            ))}
          </div>
          
          {aiInsights ? (
            <div className="space-y-6 py-4">
              {/* Summary */}
              {aiInsights.insights.summary && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-gray-700">{aiInsights.insights.summary}</p>
                </div>
              )}

              {/* Key Observations */}
              {aiInsights.insights.key_observations && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-teal-600" />
                    Key Observations
                  </h4>
                  <ul className="space-y-2">
                    {aiInsights.insights.key_observations.map((obs, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <ChevronRight className="h-4 w-4 text-teal-600 mt-1 flex-shrink-0" />
                        {obs}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {aiInsights.insights.recommendations && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Recommendations</h4>
                  <div className="space-y-3">
                    {aiInsights.insights.recommendations.map((rec, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-900">{rec.title}</h5>
                          <Badge className={
                            rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }>
                            {rec.priority}
                          </Badge>
                        </div>
                        <p className="text-gray-600 text-sm">{rec.description}</p>
                        {rec.expected_impact && (
                          <p className="text-sm text-teal-600 mt-2 font-medium">
                            Expected Impact: {rec.expected_impact}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Wins */}
              {aiInsights.insights.quick_wins && aiInsights.insights.quick_wins.length > 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Quick Wins
                  </h4>
                  <ul className="space-y-2">
                    {aiInsights.insights.quick_wins.map((win, index) => (
                      <li key={index} className="text-green-700 text-sm">• {win}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Concerns */}
              {aiInsights.insights.concerns && aiInsights.insights.concerns.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Areas of Concern
                  </h4>
                  <ul className="space-y-2">
                    {aiInsights.insights.concerns.map((concern, index) => (
                      <li key={index} className="text-amber-700 text-sm">• {concern}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center py-12">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-purple-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Analyzing your data...</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedReportsTab;
